import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EMZI_COLORS, EMZI_SPACING, EMZI_RADIUS } from '../styles/emziCI';
import axios from 'axios';
import BroadcastModeHelper from '../components/BroadcastModeHelper';
import { txt } from '../utils/bilingualText';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';


/**
 * EMZI NEXUS V6 — Announcements Page
 * WhatsApp Bulk Announcement Management
 * Mobile-first (390px) with clean EMZI CI UX
 */
const Announcements = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView] = useState('list'); // 'list', 'create', 'detail'
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);

  // RBAC
  const isManagement = user?.role === 'management_functional' || user?.role === 'management_global';
  const isAdmin = user?.role === 'admin';
  const canCreate = isManagement || isAdmin;

  useEffect(() => {
    if (!canCreate) {
      navigate('/dashboard');
    }
  }, [canCreate, navigate]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const token = secureStorage.getItem('nexus_access_token');
      const response = await axios.get(`${BACKEND_URL}/api/v3/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50 }
      });
      setAnnouncements(response.data.announcements || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setAnnouncements([]);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'list') {
      fetchAnnouncements();
    }
  }, [view, fetchAnnouncements]);

  const handleCreateNew = () => {
    setView('create');
  };

  const handleViewDetail = (announcementId) => {
    setSelectedAnnouncement(announcementId);
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedAnnouncement(null);
    fetchAnnouncements();
  };

  const handleAnnouncementCreated = (announcementId) => {
    // Route to detail view after creation
    setSelectedAnnouncement(announcementId);
    setView('detail');
  };

  return (
    <div 
      className="bg-gray-50"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div 
        className="px-5 py-4 text-white flex-shrink-0 shadow-xl"
        style={{
          background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'
        }}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border border-white border-opacity-30 shadow-lg">
              <span className="text-white font-black text-2xl">📢</span>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-wide text-white">
                Announcements
              </h1>
              <p className="text-sm text-white font-medium opacity-90">
                WhatsApp Bulk Messaging
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-3 py-1.5 text-xs font-semibold text-white border-2 border-white border-opacity-50 rounded-lg hover:bg-white hover:bg-opacity-20 transition-all"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Scrollable Container */}
      <div 
        className="max-w-7xl mx-auto px-4 py-6"
        style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 'var(--safe-area-bottom, 120px)'
        }}
      >
        {view === 'list' && (
          <AnnouncementsList
            announcements={announcements}
            onCreateNew={handleCreateNew}
            onViewDetail={handleViewDetail}
            loading={loading}
          />
        )}
        
        {view === 'create' && (
          <CreateAnnouncement
            onBack={handleBackToList}
            onSuccess={handleAnnouncementCreated}
          />
        )}
        
        {view === 'detail' && selectedAnnouncement && (
          <AnnouncementDetail
            announcementId={selectedAnnouncement}
            onBack={handleBackToList}
          />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ANNOUNCEMENTS LIST VIEW
// ============================================================================
const AnnouncementsList = ({ announcements, onCreateNew, onViewDetail, loading }) => {
  const [adapterStatus, setAdapterStatus] = React.useState(null);

  React.useEffect(() => {
    // Fetch adapter status
    const fetchAdapterStatus = async () => {
      try {
        const token = secureStorage.getItem('nexus_access_token');
        const response = await axios.get(`${BACKEND_URL}/api/v3/announcements/adapter-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdapterStatus(response.data);
      } catch (err) {
        console.error('Error fetching adapter status:', err);
      }
    };
    fetchAdapterStatus();
  }, []);

  return (
    <div className="space-y-4">
      {/* WhatsApp Adapter Status Banner */}
      {adapterStatus && (
        <div className={`p-4 rounded-xl border-2 ${
          adapterStatus.is_mock 
            ? 'bg-yellow-50 border-yellow-300' 
            : 'bg-green-50 border-green-300'
        }`}>
          <div className="flex items-start gap-3">
            <span className="text-2xl">
              {adapterStatus.is_mock ? '⚠️' : '✅'}
            </span>
            <div className="flex-1">
              <div className={`text-sm font-black mb-1 ${
                adapterStatus.is_mock ? 'text-yellow-900' : 'text-green-900'
              }`}>
                WhatsApp Adapter: {adapterStatus.adapter_mode}
              </div>
              <div className={`text-xs font-semibold mb-2 ${
                adapterStatus.is_mock ? 'text-yellow-800' : 'text-green-800'
              }`}>
                {adapterStatus.status}
              </div>
              <div className={`text-xs ${
                adapterStatus.is_mock ? 'text-yellow-700' : 'text-green-700'
              }`}>
                {adapterStatus.description}
              </div>
              {adapterStatus.is_mock && (
                <div className="mt-2 p-2 bg-yellow-100 rounded text-xs font-semibold text-yellow-900">
                  💡 {adapterStatus.warning}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Recent Announcements</h2>
          <p className="text-sm text-gray-600">Manage bulk WhatsApp messages</p>
        </div>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 shadow-md flex items-center gap-2"
        >
          <span>➕</span>
          New Announcement
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="text-4xl mb-2">⏳</div>
          <p className="text-sm font-medium text-gray-600">Loading announcements...</p>
        </div>
      ) : announcements.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="text-5xl mb-3">📢</div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No announcements yet</h3>
          <p className="text-sm text-gray-600 mb-4">
            Create your first WhatsApp announcement to notify users about new packs or updates.
          </p>
          <button
            onClick={onCreateNew}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <span>➕</span>
            Create First Announcement
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map((announcement) => (
            <AnnouncementCard
              key={announcement.announcement_id}
              announcement={announcement}
              onView={() => onViewDetail(announcement.announcement_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ANNOUNCEMENT CARD
// ============================================================================
const AnnouncementCard = ({ announcement, onView }) => {
  const statusColors = {
    queued: { bg: '#FEF3C7', text: '#92400E', icon: '⏳' },
    sending: { bg: '#DBEAFE', text: '#1E40AF', icon: '📤' },
    completed: { bg: '#D1FAE5', text: '#065F46', icon: '✅' },
    error: { bg: '#FEE2E2', text: '#991B1B', icon: '⚠️' }
  };

  const status = statusColors[announcement.status] || statusColors.queued;

  return (
    <div 
      className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onView}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-sm font-bold text-gray-900 mb-1">
            {announcement.title}
          </h3>
          <p className="text-xs text-gray-600">
            {announcement.message_template.substring(0, 100)}...
          </p>
        </div>
        <div 
          className="px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
          style={{ backgroundColor: status.bg, color: status.text }}
        >
          <span>{status.icon}</span>
          {announcement.status}
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span>👥 {announcement.total_recipients} recipients</span>
          <span>📤 {announcement.sent_count || 0} sent</span>
          {announcement.failed_count > 0 && (
            <span className="text-red-600">❌ {announcement.failed_count} failed</span>
          )}
        </div>
        <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
      </div>

      {announcement.progress > 0 && announcement.progress < 100 && (
        <div className="mt-3">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${announcement.progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CREATE ANNOUNCEMENT - 4-STEP WIZARD WITH ENHANCED UX
// ============================================================================
const CreateAnnouncement = ({ onBack, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Basic, 2: Audience, 3: Message, 4: Review (Modal)
  const [formData, setFormData] = useState({
    title: '',
    notes: '',
    audienceType: 'all',
    roles: [],
    groups: [],
    csvData: [],
    message: '',
    packIds: [],
    languageMode: 'ms',
    scheduleAt: 'now'
  });
  const [packs, setPacks] = useState([]);
  const [packSearchLoading, setPackSearchLoading] = useState(false);
  const [estimatedRecipients, setEstimatedRecipients] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [shortUrls, setShortUrls] = useState({});

  useEffect(() => {
    fetchPacks();
    fetchEstimatedRecipients();
  }, []);

  useEffect(() => {
    fetchEstimatedRecipients();
  }, [formData.audienceType, formData.roles, formData.groups]);

  useEffect(() => {
    // Generate short URLs for selected packs
    if (formData.packIds.length > 0) {
      generateShortUrls();
    }
  }, [formData.packIds]);

  const fetchPacks = async () => {
    try {
      setPackSearchLoading(true);
      const token = secureStorage.getItem('nexus_access_token');
      const response = await axios.get(`${BACKEND_URL}/api/v3/packs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100, status: 'published' }
      });
      setPacks(response.data.packs || []);
      setPackSearchLoading(false);
    } catch (err) {
      console.error('Error fetching packs:', err);
      setPackSearchLoading(false);
    }
  };

  const fetchEstimatedRecipients = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const params = {
        audience_type: formData.audienceType
      };
      if (formData.roles.length > 0) params.roles = formData.roles.join(',');
      if (formData.groups.length > 0) params.groups = formData.groups.join(',');

      // Mock estimated count for now (backend would provide actual count)
      // const response = await axios.get(`${BACKEND_URL}/api/v3/announcements/estimate-recipients`, {
      //   headers: { Authorization: `Bearer ${token}` },
      //   params
      // });
      // setEstimatedRecipients(response.data.count || 0);
      
      // Mock implementation
      let mockCount = 0;
      if (formData.audienceType === 'all') mockCount = 150;
      else if (formData.audienceType === 'roles') mockCount = formData.roles.length * 25;
      else if (formData.audienceType === 'groups') mockCount = formData.groups.length * 15;
      setEstimatedRecipients(mockCount);
    } catch (err) {
      console.error('Error fetching estimated recipients:', err);
    }
  };

  const generateShortUrls = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const urls = {};
      
      for (const packId of formData.packIds) {
        const pack = packs.find(p => p.pack_id === packId);
        if (pack) {
          // Generate short URL (mock for now)
          // const response = await axios.post(`${BACKEND_URL}/api/v3/short-urls`, {
          //   target_url: `${window.location.origin}/knowledge-packs/${packId}`,
          //   pack_id: packId
          // }, {
          //   headers: { Authorization: `Bearer ${token}` }
          // });
          // urls[packId] = response.data.short_url;
          
          // Mock short URL
          urls[packId] = `${window.location.origin}/s/${packId.substring(0, 6)}`;
        }
      }
      
      setShortUrls(urls);
    } catch (err) {
      console.error('Error generating short URLs:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = secureStorage.getItem('nexus_access_token');
      
      const payload = {
        title: formData.title,
        notes: formData.notes,
        audience: {
          type: formData.audienceType,
          roles: formData.roles,
          groups: formData.groups,
          csv_data: formData.csvData
        },
        message_template: formData.message,
        pack_ids: formData.packIds,
        language_mode: formData.languageMode,
        schedule_at: formData.scheduleAt
      };

      const response = await axios.post(`${BACKEND_URL}/api/v3/announcements`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLoading(false);
      setShowReviewModal(false);
      onSuccess(response.data.announcement_id);
    } catch (err) {
      console.error('Error creating announcement:', err);
      setError(err.response?.data?.detail || 'Failed to create announcement');
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setTestSending(true);
      setTestResult(null);

      const token = secureStorage.getItem('nexus_access_token');
      
      // Create a temporary announcement ID for test
      const tempAnnouncementId = 'test-message';
      
      await axios.post(
        `${BACKEND_URL}/api/v3/announcements/${tempAnnouncementId}/test`,
        {
          message_template: formData.message,
          phone: testPhone,
          tokens: {
            first_name: 'Ahmad',
            pack_name: packs.find(p => p.pack_id === formData.packIds[0])?.name?.en || 'Sample Pack',
            link: shortUrls[formData.packIds[0]] || 'https://example.com/pack'
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setTestResult({
        success: true,
        message: '✅ Test message sent successfully!'
      });
      setTimeout(() => setTestResult(null), 5000);
      setTestSending(false);
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || '⚠️ Test failed. Using mock adapter.'
      });
      setTimeout(() => setTestResult(null), 5000);
      setTestSending(false);
    }
  };

  const insertToken = (token) => {
    const textarea = document.getElementById('message-textarea');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.message;
      const newText = text.substring(0, start) + `{{${token}}}` + text.substring(end);
      setFormData({ ...formData, message: newText });
      
      // Focus back on textarea
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + token.length + 4, start + token.length + 4);
      }, 0);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Basic Information</h3>
              <p className="text-xs text-gray-600">Internal details for tracking</p>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Announcement Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., New Pack Launch - EMZI Care"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={{ minHeight: '44px' }}
              />
              <p className="text-xs text-gray-500 mt-1.5">💡 Internal name for tracking (not visible to recipients)</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Language Mode <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'en', label: 'English', emoji: '🇬🇧' },
                  { value: 'ms', label: 'Malay', emoji: '🇲🇾' },
                  { value: 'auto', label: 'Auto', emoji: '🤖' }
                ].map((lang) => (
                  <button
                    key={lang.value}
                    type="button"
                    onClick={() => setFormData({...formData, languageMode: lang.value})}
                    className={`px-3 py-3 text-sm font-semibold rounded-lg border-2 transition-all ${
                      formData.languageMode === lang.value
                        ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                    style={{ minHeight: '44px' }}
                  >
                    <div className="text-lg mb-0.5">{lang.emoji}</div>
                    {lang.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1.5">💡 Auto detects user's preferred language</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Internal notes or context..."
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Select Audience</h3>
              <p className="text-xs text-gray-600">Who will receive this announcement?</p>
            </div>

            {/* Estimated Recipients Badge */}
            <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">👥</span>
                  <div>
                    <div className="text-xs font-semibold text-gray-600">Estimated Recipients</div>
                    <div className="text-xl font-black text-blue-700">{estimatedRecipients}</div>
                  </div>
                </div>
                <div className="text-xs text-gray-600">
                  {formData.audienceType === 'all' && 'All users'}
                  {formData.audienceType === 'roles' && `${formData.roles.length} role(s)`}
                  {formData.audienceType === 'groups' && `${formData.groups.length} group(s)`}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              {[
                { value: 'all', label: 'All Users', desc: 'Everyone with phone numbers', icon: '👥' },
                { value: 'roles', label: 'Specific Roles', desc: 'Target by user role', icon: '🎭' },
                { value: 'groups', label: 'Groups', desc: 'Functional teams', icon: '👨‍👩‍👧‍👦' }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({...formData, audienceType: option.value, roles: [], groups: []})}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                    formData.audienceType === option.value
                      ? 'border-blue-600 bg-blue-50 shadow-sm'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                  style={{ minHeight: '60px' }}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{option.icon}</span>
                    <div className="flex-1">
                      <div className="font-bold text-sm text-gray-900">{option.label}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{option.desc}</div>
                    </div>
                    {formData.audienceType === option.value && (
                      <span className="text-blue-600 text-lg">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {formData.audienceType === 'roles' && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Roles
                </label>
                <div className="space-y-2.5">
                  {[
                    { value: 'executor', label: 'Executor', desc: 'Field agents', icon: '👤' },
                    { value: 'management_functional', label: 'Management (Functional)', desc: 'Team managers', icon: '👨‍💼' },
                    { value: 'admin', label: 'Admin', desc: 'System administrators', icon: '👑' }
                  ].map((role) => (
                    <label 
                      key={role.value} 
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 cursor-pointer transition-all"
                      style={{ minHeight: '44px' }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(role.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, roles: [...formData.roles, role.value]});
                          } else {
                            setFormData({...formData, roles: formData.roles.filter(r => r !== role.value)});
                          }
                        }}
                        className="w-5 h-5"
                        style={{ accentColor: '#0e67a2' }}
                      />
                      <span className="text-lg">{role.icon}</span>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">{role.label}</div>
                        <div className="text-xs text-gray-600">{role.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.audienceType === 'groups' && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Groups
                </label>
                <p className="text-xs text-gray-600 mb-3">CSV upload feature coming soon</p>
                <div className="p-4 bg-white rounded-lg border-2 border-dashed border-gray-300 text-center">
                  <div className="text-3xl mb-2">📁</div>
                  <p className="text-sm font-semibold text-gray-600">Upload CSV</p>
                  <p className="text-xs text-gray-500 mt-1">Drag & drop or click to upload</p>
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Compose Message</h3>
              <p className="text-xs text-gray-600">Craft your WhatsApp announcement</p>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Message Template <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message-textarea"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={6}
                placeholder="Hi {{first_name}}, check out our new {{pack_name}}! Learn more: {{link}}"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
              />
              
              {/* Token Helper Buttons */}
              <div className="mt-2">
                <div className="text-xs font-semibold text-gray-600 mb-2">Insert Variables:</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { token: 'first_name', label: 'First Name', icon: '👤' },
                    { token: 'pack_name', label: 'Pack Name', icon: '📦' },
                    { token: 'link', label: 'Link', icon: '🔗' }
                  ].map((item) => (
                    <button
                      key={item.token}
                      type="button"
                      onClick={() => insertToken(item.token)}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg border border-gray-300 transition-all flex items-center gap-1.5"
                      style={{ minHeight: '32px' }}
                    >
                      <span>{item.icon}</span>
                      <code className="font-mono">{`{{${item.token}}}`}</code>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Character Count */}
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-gray-500">
                  💡 Use variables for personalization
                </span>
                <span className={`font-semibold ${formData.message.length > 1000 ? 'text-red-600' : 'text-gray-600'}`}>
                  {formData.message.length} / 1000
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Attach Knowledge Packs (Optional)
              </label>
              <p className="text-xs text-gray-600 mb-2">Select packs to generate short links</p>
              
              {packSearchLoading ? (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <span className="text-2xl">⏳</span>
                  <p className="text-xs text-gray-600 mt-1">Loading packs...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto border-2 border-gray-300 rounded-lg p-2">
                  {packs.map((pack) => (
                    <label 
                      key={pack.pack_id} 
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-all border border-transparent hover:border-blue-200"
                      style={{ minHeight: '44px' }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.packIds.includes(pack.pack_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, packIds: [...formData.packIds, pack.pack_id]});
                          } else {
                            setFormData({...formData, packIds: formData.packIds.filter(id => id !== pack.pack_id)});
                          }
                        }}
                        className="w-5 h-5"
                        style={{ accentColor: '#0e67a2' }}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">{txt(pack.name, 'en', 'Untitled')}</div>
                        <div className="text-xs text-gray-600">
                          {pack.pack_type} • v{pack.version || '1'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {/* Selected Packs Preview with Short URLs */}
              {formData.packIds.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-xs font-semibold text-blue-900 mb-2">
                    📎 {formData.packIds.length} Pack(s) Selected
                  </div>
                  <div className="space-y-2">
                    {formData.packIds.map((packId) => {
                      const pack = packs.find(p => p.pack_id === packId);
                      return (
                        <div key={packId} className="text-xs bg-white rounded p-2 border border-blue-200">
                          <div className="font-semibold text-gray-900">{pack?.name?.en || packId}</div>
                          <div className="text-gray-600 font-mono mt-1">
                            🔗 {shortUrls[packId] || 'Generating...'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Test Message Section */}
            <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-300">
              <h4 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2">
                <span>📤</span>
                Send Test Message
              </h4>
              <p className="text-xs text-gray-600 mb-3">Test your message before sending to everyone</p>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="+60123456789"
                  className="flex-1 px-3 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ minHeight: '44px' }}
                />
                <button
                  type="button"
                  onClick={handleSendTest}
                  disabled={!testPhone || !formData.message || testSending}
                  className="px-4 py-2.5 bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  style={{ minHeight: '44px' }}
                >
                  {testSending ? '⏳ Sending...' : '📤 Test'}
                </button>
              </div>
              {testResult && (
                <div className={`mt-3 p-2.5 rounded-lg text-xs font-semibold ${testResult.success ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {testResult.message}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.title.trim().length > 0 && formData.languageMode;
      case 2:
        if (formData.audienceType === 'roles') {
          return formData.roles.length > 0;
        }
        return true;
      case 3:
        return formData.message.trim().length > 0;
      default:
        return false;
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1">
              <div className="flex items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    s <= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-blue-600' : 'bg-gray-200'}`} />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-600">
          <span>Basic</span>
          <span>Audience</span>
          <span>Message</span>
          <span>Review</span>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {renderStep()}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            ⚠️ {error}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={step === 1 ? onBack : () => setStep(step - 1)}
            className="px-4 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-all"
            style={{ minHeight: '44px' }}
          >
            {step === 1 ? '✕ Cancel' : '← Previous'}
          </button>
          
          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all"
              style={{ minHeight: '44px' }}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowReviewModal(true)}
              disabled={!canProceed()}
              className="px-6 py-2.5 bg-green-600 text-white text-sm font-bold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all flex items-center gap-2"
              style={{ minHeight: '44px' }}
            >
              <span>📋</span>
              Review & Send
            </button>
          )}
        </div>
      </div>
      
      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          formData={formData}
          packs={packs}
          shortUrls={shortUrls}
          estimatedRecipients={estimatedRecipients}
          onClose={() => setShowReviewModal(false)}
          onConfirm={handleSubmit}
          loading={loading}
          error={error}
        />
      )}
    </div>
  );
};

// ============================================================================
// REVIEW MODAL - Step 4
// ============================================================================
const ReviewModal = ({ formData, packs, shortUrls, estimatedRecipients, onClose, onConfirm, loading, error }) => {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: 'calc(90vh - env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <h3 className="text-lg font-black">Review & Send</h3>
            <p className="text-sm opacity-90">Double-check before sending</p>
          </div>
          <button
            onClick={onClose}
            className="text-white text-2xl hover:bg-white hover:bg-opacity-20 w-8 h-8 rounded-full transition-all"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div 
          className="p-6 space-y-4 overflow-y-auto flex-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Summary Card */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-1">Estimated Recipients</div>
                <div className="text-2xl font-black text-blue-700">{estimatedRecipients}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-1">Attached Packs</div>
                <div className="text-2xl font-black text-indigo-700">{formData.packIds.length}</div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="text-xs font-bold text-gray-500 mb-1 uppercase">Title</div>
            <div className="text-sm font-semibold text-gray-900">{formData.title}</div>
            {formData.notes && (
              <div className="text-xs text-gray-600 mt-2 italic">{formData.notes}</div>
            )}
          </div>

          {/* Audience */}
          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="text-xs font-bold text-gray-500 mb-2 uppercase">Audience</div>
            <div className="flex items-center gap-2">
              <span className="text-lg">
                {formData.audienceType === 'all' && '👥'}
                {formData.audienceType === 'roles' && '🎭'}
                {formData.audienceType === 'groups' && '👨‍👩‍👧‍👦'}
              </span>
              <div className="text-sm text-gray-900">
                {formData.audienceType === 'all' && 'All Users'}
                {formData.audienceType === 'roles' && (
                  <div className="flex flex-wrap gap-1">
                    {formData.roles.map(role => (
                      <span key={role} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                        {role.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                )}
                {formData.audienceType === 'groups' && `${formData.groups.length} group(s)`}
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="text-xs font-bold text-gray-500 mb-2 uppercase">Message Preview</div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-900 font-mono whitespace-pre-wrap break-words">
                {formData.message}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                {formData.languageMode.toUpperCase()}
              </span>
              <span className="text-xs text-gray-600">
                {formData.message.length} characters
              </span>
            </div>
          </div>

          {/* Selected Packs */}
          {formData.packIds.length > 0 && (
            <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
              <div className="text-xs font-bold text-gray-500 mb-2 uppercase">Attached Knowledge Packs</div>
              <div className="space-y-2">
                {formData.packIds.map((packId) => {
                  const pack = packs.find(p => p.pack_id === packId);
                  return (
                    <div key={packId} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-sm font-semibold text-gray-900">{pack?.name?.en || packId}</div>
                      <div className="text-xs text-gray-600 font-mono mt-1">
                        🔗 {shortUrls[packId] || 'Generating...'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">ℹ️</span>
              <div>
                <div className="text-xs font-bold text-yellow-900 mb-1">Sending Details</div>
                <div className="text-xs text-yellow-800">
                  • Messages sent at <strong>10 msg/sec</strong> with rate limiting<br/>
                  • Delivery reports available in real-time<br/>
                  • Using <strong>Mock WhatsApp Adapter</strong> (safe for testing)
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-2">
                <span className="text-lg">⚠️</span>
                <div>
                  <div className="text-xs font-bold text-red-900">Error</div>
                  <div className="text-xs text-red-700 mt-1">{error}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div 
          className="sticky bottom-0 bg-gray-50 px-6 rounded-b-2xl flex items-center justify-between border-t border-gray-200 flex-shrink-0"
          style={{ 
            paddingTop: '16px',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))'
          }}
        >
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2.5 text-sm font-semibold text-gray-600 hover:text-gray-800 disabled:opacity-50"
            style={{ minHeight: '44px' }}
          >
            ← Edit
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-8 py-2.5 bg-gradient-to-r from-green-600 to-green-500 text-white text-sm font-bold rounded-lg hover:from-green-700 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all flex items-center gap-2"
            style={{ minHeight: '44px' }}
          >
            {loading ? (
              <>
                <span className="animate-spin">⏳</span>
                Sending...
              </>
            ) : (
              <>
                <span>📤</span>
                Send Announcement
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ANNOUNCEMENT DETAIL VIEW - WITH REAL-TIME POLLING
// ============================================================================
const AnnouncementDetail = ({ announcementId, onBack }) => {
  const [announcement, setAnnouncement] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch announcement status
  const fetchAnnouncementStatus = useCallback(async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/v3/announcements/${announcementId}/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAnnouncement(response.data.announcement);
      
      // Stop auto-refresh if completed or error
      if (response.data.announcement.status === 'completed' || response.data.announcement.status === 'error') {
        setAutoRefresh(false);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching announcement status:', err);
      setLoading(false);
    }
  }, [announcementId]);

  // Fetch recipients
  const fetchRecipients = useCallback(async () => {
    try {
      setRecipientsLoading(true);
      const token = secureStorage.getItem('nexus_access_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/v3/announcements/${announcementId}/recipients`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, limit: 50 }
        }
      );
      setRecipients(response.data.recipients || []);
      setTotalPages(Math.ceil((response.data.total || 0) / 50));
      setRecipientsLoading(false);
    } catch (err) {
      console.error('Error fetching recipients:', err);
      setRecipientsLoading(false);
    }
  }, [announcementId, page]);

  // Initial fetch
  useEffect(() => {
    fetchAnnouncementStatus();
    fetchRecipients();
  }, [fetchAnnouncementStatus, fetchRecipients]);

  // Auto-refresh polling (every 3 seconds)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchAnnouncementStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchAnnouncementStatus]);

  // Refetch recipients when page changes
  useEffect(() => {
    if (announcement) {
      fetchRecipients();
    }
  }, [page, fetchRecipients]);

  const statusColors = {
    queued: { bg: '#FEF3C7', text: '#92400E', icon: '⏳', label: 'Queued' },
    sending: { bg: '#DBEAFE', text: '#1E40AF', icon: '📤', label: 'Sending' },
    completed: { bg: '#D1FAE5', text: '#065F46', icon: '✅', label: 'Completed' },
    error: { bg: '#FEE2E2', text: '#991B1B', icon: '⚠️', label: 'Error' }
  };

  const recipientStatusColors = {
    queued: { bg: '#FEF3C7', text: '#92400E', icon: '⏳' },
    sent: { bg: '#D1FAE5', text: '#065F46', icon: '✅' },
    failed: { bg: '#FEE2E2', text: '#991B1B', icon: '❌' }
  };

  if (loading || !announcement) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="text-5xl mb-3">⏳</div>
          <p className="text-sm font-semibold text-gray-600">Loading announcement...</p>
        </div>
      </div>
    );
  }

  const status = statusColors[announcement.status] || statusColors.queued;
  const progress = Math.round((announcement.sent_count / announcement.total_recipients) * 100) || 0;

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
        style={{ minHeight: '44px' }}
      >
        <span>←</span>
        Back to List
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {/* Title and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 mb-2">{announcement.title}</h2>
            <p className="text-xs text-gray-600">
              Created {new Date(announcement.created_at).toLocaleString()}
            </p>
          </div>
          <div 
            className="px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
            style={{ backgroundColor: status.bg, color: status.text }}
          >
            <span className="text-lg">{status.icon}</span>
            {status.label}
          </div>
        </div>

        {/* Auto-refresh indicator */}
        {autoRefresh && (
          <div className="mb-4 p-2 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-2">
            <span className="animate-pulse text-blue-600">🔄</span>
            <span className="text-xs font-semibold text-blue-700">Auto-refreshing every 3s</span>
          </div>
        )}
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <div className="text-3xl font-black text-gray-900">{announcement.total_recipients}</div>
            <div className="text-xs font-semibold text-gray-600 mt-1">Total</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
            <div className="text-3xl font-black text-green-700">{announcement.sent_count || 0}</div>
            <div className="text-xs font-semibold text-gray-600 mt-1">Sent</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200">
            <div className="text-3xl font-black text-red-700">{announcement.failed_count || 0}</div>
            <div className="text-xs font-semibold text-gray-600 mt-1">Failed</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
            <div className="text-3xl font-black text-blue-700">{progress}%</div>
            <div className="text-xs font-semibold text-gray-600 mt-1">Progress</div>
          </div>
        </div>

        {/* Progress Bar */}
        {progress > 0 && progress < 100 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
              <span className="font-semibold">Sending Progress</span>
              <span>{announcement.sent_count} / {announcement.total_recipients}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 shadow-sm"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Message Preview */}
        <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
          <div className="text-xs font-bold text-gray-500 mb-2 uppercase">Message Template</div>
          <div className="text-sm text-gray-900 font-mono whitespace-pre-wrap break-words">
            {announcement.message_template}
          </div>
        </div>
      </div>

      {/* WhatsApp Broadcast Mode Helper */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <BroadcastModeHelper
          messageTemplate={announcement.message_template}
          packIds={announcement.pack_ids || []}
          recipients={recipients}
          announcementId={announcementId}
          onComplete={() => {
            fetchAnnouncementStatus();
            setAutoRefresh(false);
          }}
        />
      </div>

      {/* Recipients Table */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Recipients</h3>
          <button
            onClick={fetchRecipients}
            disabled={recipientsLoading}
            className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-all disabled:opacity-50"
          >
            {recipientsLoading ? '⏳ Loading...' : '🔄 Refresh'}
          </button>
        </div>
        
        {recipientsLoading && recipients.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-4xl mb-2">⏳</div>
            <p className="text-sm font-semibold text-gray-600">Loading recipients...</p>
          </div>
        ) : recipients.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-4xl mb-2">📭</div>
            <p className="text-sm font-semibold text-gray-600">No recipients found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recipients.map((recipient, idx) => {
              const recipientStatus = recipientStatusColors[recipient.status] || recipientStatusColors.queued;
              return (
                <div 
                  key={`item-${idx}`}
                  className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-all"
                >
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900">{recipient.name || 'User'}</div>
                    <div className="text-xs text-gray-600 font-mono">{recipient.phone || 'N/A'}</div>
                  </div>
                  <div 
                    className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5"
                    style={{ backgroundColor: recipientStatus.bg, color: recipientStatus.text }}
                  >
                    <span>{recipientStatus.icon}</span>
                    {recipient.status}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ minHeight: '44px' }}
            >
              ← Previous
            </button>
            <span className="text-sm font-semibold text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ minHeight: '44px' }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Announcements;
